import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Phone, PhoneCall } from "lucide-react";
import { VoiceAgent, VoiceAgentStats } from "@/services/voiceAgentService";
import { getConsistentColor } from "@/lib/utils";
import PhoneNumberStatusCard from "@/components/voice-agents/PhoneNumberStatusCard";
import { StatsLoader } from "@/components/ui/StatsLoader";

interface VoiceAgentCardProps {
  agent: VoiceAgent;
  stats: VoiceAgentStats;
  onNavigateToDetail: (id: string) => void;
  onViewCalls: (e: React.MouseEvent, agentId: string) => void;
  onAgentUpdate: (updatedAgent: VoiceAgent) => void;
  showPhoneStatus?: boolean;
  isLoadingStats?: boolean;
}

export default function VoiceAgentCard({
  agent,
  stats,
  onNavigateToDetail,
  onViewCalls,
  onAgentUpdate,
  showPhoneStatus = true,
  isLoadingStats = false,
}: VoiceAgentCardProps) {
  // Function to get gradient color based on agent ID
  const getAgentGradient = (id: string): string => {
    const colors = [
      "from-blue-100 to-blue-300",
      "from-purple-100 to-purple-300",
      "from-pink-100 to-pink-300",
      "from-green-100 to-green-300",
      "from-amber-100 to-amber-300",
      "from-cyan-100 to-cyan-300",
      "from-indigo-100 to-indigo-300",
      "from-rose-100 to-rose-300",
      "from-emerald-100 to-emerald-300",
      "from-orange-100 to-orange-300",
    ];

    const index =
      id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      colors.length;
    return `bg-gradient-to-br ${colors[index]}`;
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const gradientClass = getAgentGradient(agent.id);

  // Calculate stats - ensure we have the right structure
  const totalCalls = stats.total_calls || 0;
  const last30Days =
    (stats as any).stats?.last_30_days || stats.last_30_days || 0;
  const last24Hours = stats.calls_today || (stats as any).last_24_hours || 0;

  return (
    <Card
      className="group hover:shadow-md transition-all duration-300 cursor-pointer border border-gray-200 hover:border-gray-300 overflow-hidden"
      onClick={() => onNavigateToDetail(agent.id)}
    >
      {/* Colored banner */}
      <div
        className={`h-20 w-full ${gradientClass.replace("bg-gradient-to-br", "bg-gradient-to-r")}`}
      ></div>

      <CardContent className="p-5 relative">
        {/* Avatar overlapping banner */}
        <div className="absolute -top-12 left-5">
          <div className={`relative p-0.5 rounded-full ${gradientClass}`}>
            <Avatar className="h-32 w-32 border-2 border-white">
              <AvatarImage src={agent.avatar_url || ""} />
              <AvatarFallback
                className="text-xl font-bold text-white"
                style={{ backgroundColor: `${getConsistentColor(agent.id)}80` }}
              >
                {getInitials(agent.agent_name)}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>

        {/* Content - left aligned */}
        <div className="text-left">
          {/* Avatar and Agent Info Row */}
          <div className="flex items-center gap-6 mb-8">
            {/* Space for avatar (since it's positioned absolute) */}
            <div className="w-32 flex-shrink-0"></div>

            {/* Agent Info Column */}
            <div className="flex-1 min-w-0">
              {/* Main Title (Agent Name) */}
              <h3 className="text-2xl font-bold text-gray-900 truncate">
                {agent.agent_name}
              </h3>

              {/* Subtitle (Title/Description) */}
              <p className="text-base text-gray-600 truncate">
                {agent.title || agent.description || "Voice AI Assistant"}
              </p>

              {/* Phone Number Badge */}
              {agent.phone_number && (
                <div className="mt-2">
                  <Badge variant="outline" className="text-xs">
                    <Phone className="h-3 w-3 mr-1" />
                    Phone Enabled
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Stats without header */}
          <div className="mb-8">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-sm text-gray-500 mb-1">Total calls</div>
                <div className="text-base font-medium text-gray-900">
                  {isLoadingStats ? (
                    <StatsLoader type="metric" width="40px" height="20px" />
                  ) : (
                    totalCalls
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Last 30 days</div>
                <div className="text-base font-medium text-gray-900">
                  {isLoadingStats ? (
                    <StatsLoader type="metric" width="35px" height="20px" />
                  ) : (
                    last30Days
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Last 24h</div>
                <div className="text-base font-medium text-gray-900">
                  {isLoadingStats ? (
                    <StatsLoader type="metric" width="30px" height="20px" />
                  ) : (
                    last24Hours
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 items-center">
            <Button
              onClick={(e) => onViewCalls(e, agent.id)}
              variant="outline"
              size="sm"
            >
              <PhoneCall className="mr-2 h-4 w-4" />
              View Calls
            </Button>

            {/* Phone Number Widget - only show if enabled */}
            {showPhoneStatus && (
              <div className="flex-1" onClick={(e) => e.stopPropagation()}>
                <PhoneNumberStatusCard
                  agent={agent}
                  onAgentUpdate={onAgentUpdate}
                />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
